/**
 * Storage source form → API payload mapping.
 *
 * The Storage Sources page collects form state as a flat object (SourceFormData)
 * and must submit the nested shape validated by the backend CreateSourceSchema
 * (packages/storage-core/src/schemas/source.schema.ts):
 *
 *   {
 *     name, type, dockerImage, supportsByteRange, strategy,
 *     chunkConfig: { enabled, chunkSizeMB },
 *     proxy: { type, ... }, readEndpoint,
 *     config: { type, ... }   // discriminated by `type` (s3 | ftp | http)
 *   }
 *
 * Keeping the mapping in a pure function lets unit tests verify the exact
 * contract against the backend schema.
 */

import type { StorageProxyType, StorageSource } from '@/types';

export interface SourceFormData {
  name: string;
  type: string;
  dockerImage: string;
  supportsByteRange: boolean;
  strategy: string;
  chunkEnabled: boolean;
  chunkSizeMB: number;
  proxyType: StorageProxyType;
  proxyConfig: Record<string, unknown>;
  readEndpoint: string;
  config: Record<string, unknown>;
}

export const DEFAULT_DOCKER_IMAGE = 'ghcr.io/cluster/storage-adapter:latest';

export const defaultFormData: SourceFormData = {
  name: '',
  type: '',
  dockerImage: DEFAULT_DOCKER_IMAGE,
  supportsByteRange: false,
  strategy: 'least_used',
  chunkEnabled: false,
  chunkSizeMB: 5,
  proxyType: 'none',
  proxyConfig: {},
  readEndpoint: '',
  config: {},
};

// Strip empty values so optional fields don't fail backend validation
// (e.g. empty endpoint/tags/headers would not parse against the Zod schemas).
export function pruneEmptyValues(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== undefined && value !== null && value !== '' && !Number.isNaN(value),
    ),
  );
}

/**
 * Normalize a form value for a `record`-typed config field (tags/headers/...).
 *
 * The backend expects `Record<string, string>` objects, so:
 * - objects pass through unchanged (values coerced to strings to satisfy
 *   `z.record(z.string(), z.string())`);
 * - JSON strings are parsed into an object;
 * - invalid JSON, empty objects (`{}`), empty strings and primitives are
 *   dropped (`undefined`) so the field is omitted from the payload instead of
 *   failing the backend schema.
 */
export function normalizeRecordValue(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return undefined; // empty object → omit
    return Object.fromEntries(entries.map(([key, v]) => [key, String(v)]));
  }
  if (typeof value !== 'string') return undefined; // primitives → not a record
  const trimmed = value.trim();
  if (!trimmed) return undefined; // empty string → omit
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed as Record<string, unknown>);
      if (entries.length === 0) return undefined; // "{}" → omit empty record
      return Object.fromEntries(entries.map(([key, v]) => [key, String(v)]));
    }
    return undefined; // parsed to a primitive (e.g. "123") → not a record
  } catch {
    return undefined; // malformed JSON → omit (backend would reject the raw string)
  }
}

/**
 * Build the create/update payload for a storage source from the flat form state.
 * Mirrors the nested shape validated by CreateSourceSchema.
 *
 * `recordFieldNames` are the names of record-typed config fields (tags, headers,
 * readHeaders). Their values are normalized via `normalizeRecordValue` so the
 * payload always contains `Record<string,string>` objects (never plain strings).
 */
export function buildStorageSourcePayload(
  formData: SourceFormData,
  recordFieldNames: string[] = [],
): Partial<StorageSource> {
  const recordFields = new Set(recordFieldNames);
  const config = pruneEmptyValues(
    Object.fromEntries(
      Object.entries(formData.config).map(([key, value]) => [
        key,
        recordFields.has(key) ? normalizeRecordValue(value) : value,
      ]),
    ),
  );
  return {
    name: formData.name,
    type: formData.type,
    dockerImage: formData.dockerImage,
    supportsByteRange: formData.supportsByteRange,
    strategy: 'least_used',
    chunkConfig: {
      enabled: formData.chunkEnabled,
      chunkSizeMB: formData.chunkSizeMB,
    },
    proxy:
      formData.proxyType === 'none'
        ? { type: 'none' }
        : { type: formData.proxyType, ...pruneEmptyValues(formData.proxyConfig) },
    readEndpoint: formData.readEndpoint || undefined,
    config: { type: formData.type, ...config },
  };
}
